import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { delay } from 'bluebird'
import { isEmpty, isNil, map, reduce } from 'lodash'
import { Controller, useForm } from 'react-hook-form'

import routes from '../../../../routers'
import { SELECT_RECIPE_PROPS, STEPS_ORDER_BOOK } from '../../../../utils/constants'
import { saveCookbook } from '../../../../api/cookbook'
import { getRecipesForOrder } from '../../../../api/recipe'
import { chooseAllRecipes, countNumberRecipes, recipesOrder } from '../../../../utils/helpers'
import RecipeCategory from '../RecipeCategory'
import CustomTitle from '../CustomTitle'
import Footer from '../Footer'

import styles from './SecondStep.module.css'

// Same as the build spec and the server (COOKBOOK_MIN_RECIPES)
const MIN_RECIPES = 12
const selectRecipeProps = {
  ...SELECT_RECIPE_PROPS,
  rules: {
    required: true,
    validate: { value: (value) => countNumberRecipes(value) >= MIN_RECIPES },
  },
}

const SecondStep = ({ orderUrlData }) => {
  const [categories, setCategories] = useState({})
  const [countRecipes, setCountRecipes] = useState('')
  const buttonRef = useRef(null)
  const navigate = useNavigate()
  const { control, reset, handleSubmit, watch, formState, clearErrors } = useForm()

  const orderInfo = watch()?.order
  const { groupId, orderId } = orderUrlData
  const countSelectedRecipe = countNumberRecipes(orderInfo)
  const isSelectAllRecipes = countRecipes === countSelectedRecipe

  const setAllRecipes = (isActive) => {
    const newData = chooseAllRecipes({ formData: orderInfo, data: categories }, isActive)
    reset({ order: newData })
  }

  const onSubmit = ({ order }) => {
    buttonRef.current.disabled = true
    const categories = reduce(
      order,
      (res, { recipes, categoryId }, name) =>
        isEmpty(recipes) ? res : [...res, { category_id: categoryId, name, recipes_ids: recipes }],
      []
    )
    saveCookbook({ id: orderId, categories })
      .then(({ data }) => {
        const newUrlState = { ...orderUrlData, orderId: data.id }
        navigate('.', { state: newUrlState, replace: true })

        return newUrlState
      })
      .then((newUrlState) => {
        buttonRef.current.disabled = false
        navigate('.', {
          state: { ...newUrlState, currentStep: STEPS_ORDER_BOOK['THIRD_STEP'] },
        })
      })
      .catch(() => (buttonRef.current.disabled = false))
  }

  useEffect(() => {
    getRecipesForOrder(groupId, orderId)
      .then(({ data }) => {
        const resetData = recipesOrder({ data, isReset: true })
        const categories = recipesOrder({ data })
        const countRecipes = reduce(
          categories,
          (res, { recipes }) => [...res, ...recipes],
          []
        ).length

        setCategories(categories)
        setCountRecipes(countRecipes)
        reset({ order: resetData }, { keepDirty: false })
      })
      .catch(() => navigate(routes.purchaseBook))
  }, [])

  useEffect(() => {
    if (!isNil(formState.errors.order)) {
      delay(5000).then(clearErrors)
    }
  }, [formState.errors.order])

  const loaded = countRecipes !== ''
  const chapters = map(categories, (item, key) => ({ ...item, key })).filter(
    (item) => item?.recipes?.length
  )
  const toGo = Math.max(0, MIN_RECIPES - countSelectedRecipe)

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="cbStep">
      <section className="cbSection">
        <div className="cbSectionHead">
          <CustomTitle
            title="Choose your recipes"
            hoverText="Each category becomes a chapter in your book, and you can rename it with the pencil. Pick at least 12 recipes."
          />
          {countRecipes ? (
            <button
              type="button"
              className="cbSecondaryButton"
              onClick={() => setAllRecipes(!isSelectAllRecipes)}
            >
              {isSelectAllRecipes ? 'Untick all' : 'Choose all'}
            </button>
          ) : null}
        </div>
        {loaded && countRecipes ? (
          <div className={styles.meter}>
            <div className={styles.meterBar}>
              <span
                className={toGo ? '' : styles.meterFull}
                style={{ width: `${Math.min(100, (countSelectedRecipe / MIN_RECIPES) * 100)}%` }}
              />
            </div>
            <p className={styles.meterText}>
              <b>
                {countSelectedRecipe} of {countRecipes}
              </b>{' '}
              recipes chosen
              {toGo ? `, ${toGo} more to reach the 12 a book needs` : ''}
            </p>
          </div>
        ) : null}
        {!loaded ? <p className="cbMuted">Loading this group’s recipes…</p> : null}
        {loaded && !countRecipes ? (
          <p className="cbMuted">
            This group doesn’t have any recipes yet. Add a few, then come back to make a book.
          </p>
        ) : null}
      </section>

      <Controller
        {...selectRecipeProps}
        control={control}
        render={({ field: { onChange, value } }) =>
          chapters.map((item, index) => (
            <RecipeCategory
              key={item.categoryId}
              name={item.key}
              value={value}
              onChange={onChange}
              data={item.recipes}
              setCategories={setCategories}
              categoryId={item.categoryId}
              defaultOpen={index === 0}
            />
          ))
        }
      />

      <Footer
        replace
        backTo={-1}
        errorMessage={`Choose at least ${MIN_RECIPES} recipes`}
        submitButtonRef={buttonRef}
        errorShow={!isNil(formState.errors.order)}
        note={
          <span className="cbActionNote">
            <b>{countSelectedRecipe}</b> chosen
          </span>
        }
        buttonText="Next: cover & details"
      />
    </form>
  )
}

export default SecondStep
